import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/contexts/AuthContext";
import { PageHeader, PageBody, ErrorBlock, fmtCurrency } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Package, Plus, Trash2, ExternalLink, Wand2, Store, StoreIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PLATFORMS = ["amazon", "tiktok_shop", "clickbank", "impact", "etsy", "shopify", "ebay", "other"];

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/products`);
      setProducts(r.data);
    } catch (e) {
      toast.error("Failed to load products");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const extract = async () => {
    if (!url) return;
    setExtracting(true);
    setError(null);
    setDraft(null);
    try {
      const r = await axios.post(`${API}/products/extract`, { url });
      const d = r.data;
      setDraft({
        source_url: d.source_url || url,
        affiliate_url: "",
        title: d.title || "",
        description: d.description || "",
        image_url: d.image_url || "",
        price: d.price ?? "",
        currency: d.currency || "USD",
        source_platform: d.source_platform_guess || d.platform_guess || "other",
        commission_rate: "",
        tags: [],
        notes: "",
      });
    } catch (e) {
      setError(e?.response?.data || { error: "extract_failed", detail: formatApiError(e?.response?.data?.detail) });
    } finally {
      setExtracting(false);
    }
  };

  const openManualEntry = () => {
    setError(null);
    setDraft({
      source_url: url || "",
      affiliate_url: "",
      title: "",
      description: "",
      image_url: "",
      price: "",
      currency: "USD",
      source_platform: "other",
      commission_rate: "",
      tags: [],
      notes: "",
    });
  };

  const save = async () => {
    try {
      const body = {
        ...draft,
        price: draft.price === "" ? null : Number(draft.price),
        commission_rate: draft.commission_rate === "" ? null : Number(draft.commission_rate),
      };
      await axios.post(`${API}/products`, body);
      toast.success("Product saved");
      setOpen(false);
      setUrl("");
      setDraft(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const goGenerate = (p) => {
    nav("/app/generate", { state: { product: p } });
  };

  const toggleStorefront = async (p) => {
    const next = !p.public_storefront;
    // Optimistic
    setProducts((arr) => arr.map((x) => x.id === p.id ? { ...x, public_storefront: next } : x));
    try {
      await axios.post(`${API}/storefront/products/${p.id}/toggle`,
                       { public_storefront: next });
      toast.success(next ? "Added to public storefront" : "Removed from storefront");
    } catch (e) {
      // Revert
      setProducts((arr) => arr.map((x) => x.id === p.id ? { ...x, public_storefront: !next } : x));
      toast.error(formatApiError(e?.response?.data?.detail) || "Toggle failed");
    }
  };

  const closeModal = () => {
    setOpen(false);
    setUrl("");
    setDraft(null);
    setError(null);
  };

  return (
    <>
      <PageHeader
        icon={Package}
        title="Product Library"
        subtitle="Save affiliate products with extracted metadata + your tracking link."
        testid="products-header"
        actions={
          <Button
            onClick={() => setOpen(true)}
            className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
            data-testid="add-product-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Product
          </Button>
        }
      />

      <PageBody>
        {products.length === 0 ? (
          <div className="ap-panel p-8 text-center" data-testid="products-empty">
            <Package className="h-8 w-8 text-zinc-700 mx-auto mb-2" />
            <div className="text-sm text-zinc-400">No products yet.</div>
            <div className="text-xs text-zinc-600 mt-1">Click <span className="mono">Add Product</span> and paste any product URL.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="products-grid">
            {products.map((p) => (
              <div key={p.id} className="ap-panel overflow-hidden flex flex-col" data-testid={`product-card-${p.id}`}>
                <div className="aspect-[4/3] bg-zinc-900 overflow-hidden">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-700">
                      <Package className="h-8 w-8" />
                    </div>
                  )}
                </div>
                <div className="p-3 flex-1 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold truncate" title={p.title} data-testid={`product-title-${p.id}`}>{p.title}</h3>
                    <Badge variant="outline" className="border-zinc-700 mono text-[10px] text-zinc-400 shrink-0">{p.source_platform}</Badge>
                  </div>
                  <div className="text-[11px] text-zinc-500 line-clamp-2">{p.description}</div>
                  <div className="flex items-center justify-between mt-auto pt-2">
                    <div className="mono text-sm text-amber-300">{fmtCurrency(p.price, p.currency)}</div>
                    <div className="flex items-center gap-1">
                      <a href={p.affiliate_url} target="_blank" rel="noreferrer" className="p-1.5 rounded-md hover:bg-zinc-900 text-zinc-400 hover:text-emerald-400" title="Open affiliate URL">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                      <button onClick={() => toggleStorefront(p)}
                              className={`p-1.5 rounded-md hover:bg-zinc-900 ${p.public_storefront ? "text-sky-400 bg-sky-500/10" : "text-zinc-400 hover:text-sky-400"}`}
                              title={p.public_storefront ? "Visible in public storefront — click to hide" : "Add to public storefront"}
                              data-testid={`product-storefront-toggle-${p.id}`}>
                        <Store className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => goGenerate(p)} className="p-1.5 rounded-md hover:bg-zinc-900 text-zinc-400 hover:text-emerald-400" title="Generate video" data-testid={`product-generate-${p.id}`}>
                        <Wand2 className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => remove(p.id)} className="p-1.5 rounded-md hover:bg-zinc-900 text-zinc-400 hover:text-rose-400" title="Delete" data-testid={`product-delete-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </PageBody>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeModal())}>
        <DialogContent className="max-w-2xl bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="add-product-modal">
          <DialogHeader>
            <DialogTitle>Add product</DialogTitle>
          </DialogHeader>

          {!draft && (
            <div className="space-y-3" data-testid="extract-step">
              <Label className="text-zinc-300">Product URL</Label>
              <div className="flex gap-2">
                <Input
                  value={url} onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.amazon.com/dp/..."
                  className="bg-zinc-900 border-zinc-700 text-zinc-100"
                  data-testid="extract-url-input"
                />
                <Button onClick={extract} disabled={extracting || !url}
                  className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
                  data-testid="extract-btn">
                  {extracting ? "Extracting…" : "Extract"}
                </Button>
              </div>
              <div className="text-[11px] text-zinc-500">We fetch the page, parse metadata, and ask gpt-4o-mini for a clean summary.</div>
              {error && (
                <>
                  <ErrorBlock data={error} testid="extract-error" />
                  <Button
                    type="button"
                    onClick={openManualEntry}
                    variant="outline"
                    className="w-full border-emerald-700/50 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-900/40"
                    data-testid="manual-entry-btn"
                  >
                    Enter product details manually
                  </Button>
                </>
              )}
            </div>
          )}

          {draft && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 ap-scroll" data-testid="confirm-step">
              <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-3">
                {draft.image_url ? (
                  <img src={draft.image_url} alt={draft.title} className="w-[120px] h-[120px] rounded-md object-cover bg-zinc-900 border border-zinc-800" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-[120px] h-[120px] rounded-md bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-700"><Package className="h-6 w-6" /></div>
                )}
                <div className="space-y-2">
                  <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Title" className="bg-zinc-900 border-zinc-700" data-testid="draft-title-input" />
                  <Textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={2} placeholder="Description" className="bg-zinc-900 border-zinc-700" data-testid="draft-description-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-zinc-400 text-xs">Price</Label>
                  <Input value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} placeholder="29.99" className="mt-1 bg-zinc-900 border-zinc-700" data-testid="draft-price-input" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-zinc-400 text-xs">Currency</Label>
                  <Input value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value.toUpperCase() })} placeholder="USD" className="mt-1 bg-zinc-900 border-zinc-700" data-testid="draft-currency-input" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-zinc-400 text-xs">Commission %</Label>
                  <Input value={draft.commission_rate} onChange={(e) => setDraft({ ...draft, commission_rate: e.target.value })} placeholder="8" className="mt-1 bg-zinc-900 border-zinc-700" data-testid="draft-commission-input" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-zinc-400 text-xs">Platform</Label>
                  <Select value={draft.source_platform} onValueChange={(v) => setDraft({ ...draft, source_platform: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-700" data-testid="draft-platform-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                      {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-zinc-400 text-xs">Image URL</Label>
                <Input value={draft.image_url} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-700" data-testid="draft-image-input" />
              </div>

              <div>
                <Label className="text-zinc-400 text-xs">Your affiliate URL (required)</Label>
                <Input
                  value={draft.affiliate_url}
                  onChange={(e) => setDraft({ ...draft, affiliate_url: e.target.value })}
                  placeholder="Paste your tagged affiliate link"
                  className="mt-1 bg-zinc-900 border-zinc-700 text-emerald-300"
                  data-testid="draft-affiliate-input"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Notes</Label>
                <Textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} rows={2} className="mt-1 bg-zinc-900 border-zinc-700" data-testid="draft-notes-input" />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeModal} className="border-zinc-700 bg-zinc-900 text-zinc-200" data-testid="cancel-add-product-btn">Cancel</Button>
            {draft && (
              <Button
                onClick={save}
                disabled={!draft.affiliate_url || !draft.title}
                className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
                data-testid="save-product-btn"
              >
                Save product
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
